import React, { useState } from 'react'
import { YStack, XStack } from 'tamagui'

import { Dialog } from '@newComponents/Dialog/Dialog'
import { Text } from '@newComponents/Text'
import { Button } from '@newComponents/Button/Button'
import { ExternalLink } from '@newComponents/Link'
import { HOST_NAME } from '@data/config/url'
import GoogleCalendarIcon from '@newComponents/Icon/icons/google-calendar-icon'
import MacOSCalendarIcon from '@newComponents/Icon/icons/macos-calendar'

export const CalendarSubscribeDialog = ({
  open,
  onOpenChange,
  slug,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  slug: string
}) => {
  const [copied, setCopied] = useState(false)

  const webcalUrl = `webcal://${HOST_NAME.replace(/^https?:\/\//, '')}/${slug}/calendar`
  const httpsUrl = `${HOST_NAME}/${slug}/calendar`
  const googleCalendarUrl = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`

  const handleCopy = async () => {
    await navigator.clipboard.writeText(webcalUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const content = (
    <YStack gap='$4'>
      <Dialog.Title>Subscribe to Calendar</Dialog.Title>

      <Text size='$5'>
        Subscribe to this calendar to automatically receive event updates in
        your calendar app.
      </Text>

      <YStack gap='$3'>
        <ExternalLink
          href={googleCalendarUrl}
          hoverStyle={{ textDecorationLine: 'none' }}
        >
          <XStack gap='$3' alignItems='center'>
            <GoogleCalendarIcon />
            <Text size='$5' fontWeight='$5'>
              Google Calendar
            </Text>
          </XStack>
        </ExternalLink>

        <ExternalLink
          href={webcalUrl}
          hoverStyle={{ textDecorationLine: 'none' }}
        >
          <XStack gap='$3' alignItems='center'>
            <MacOSCalendarIcon />
            <Text size='$5' fontWeight='$5'>
              Apple Calendar
            </Text>
          </XStack>
        </ExternalLink>
      </YStack>

      <YStack gap='$2'>
        <Text size='$4' color='$colorMuted'>
          Or copy the URL to add manually:
        </Text>

        <XStack gap='$2' alignItems='center'>
          <XStack
            flex={1}
            backgroundColor='$backgroundAccent'
            borderRadius='$2'
            paddingHorizontal='$3'
            paddingVertical='$2'
          >
            <Text size='$4' numberOfLines={1}>
              {httpsUrl}
            </Text>
          </XStack>

          <Button secondary onPress={handleCopy}>
            <Button.Text secondary size='$4'>
              {copied ? 'Copied!' : 'Copy'}
            </Button.Text>
          </Button>
        </XStack>
      </YStack>
    </YStack>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Content width={440} maxWidth='90vw'>
          {content}
        </Dialog.Content>
      </Dialog.Portal>

      <Dialog.Adapt when='xs'>
        <Dialog.Sheet>
          <Dialog.Sheet.Handle />
          <Dialog.Sheet.Frame padding='$6' gap='$4'>
            <Dialog.Sheet.ScrollView>{content}</Dialog.Sheet.ScrollView>
          </Dialog.Sheet.Frame>
        </Dialog.Sheet>
      </Dialog.Adapt>
    </Dialog>
  )
}
